import React, { useEffect, useState, useCallback, useRef } from 'react'
import api from '../api/client'
import type { Shift } from '../types'
import { useNavigate } from 'react-router-dom'
import { Calendar, Clock, MapPin, Users, Plus, Search, Filter, Trash2, Edit, Copy, Download, Award, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { format, startOfDay, isSameDay, addDays, subDays, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns'
import { formatDayDateToLocal, formatTimeRangeToLocal, isTodayInLocal, isUpcomingInLocal } from '../utils/timezone'
import ShiftDetailsModal from '../components/ShiftDetailsModal'
import DeleteConfirmModal from '../components/DeleteConfirmModal'
import DuplicateShiftModal from '../components/DuplicateShiftModal'
import ClaimConfirmModal from '../components/ClaimConfirmModal'
import { useAuth } from '../contexts/AuthContext'
import { useLocale } from '../contexts/LocaleContext'
import { Alert, Button, CalendarMonth, Capacity, DayView, Input, Select, StatusBadge, Tabs, Tag } from '@rallyshift/design-system'
import type { CalendarDay, DayViewShift } from '@rallyshift/design-system'
import { CAPACITY_LABELS, statusLabel } from '../utils/status'
import * as XLSX from 'xlsx'

const ShiftsPage: React.FC = () => {
  const { profile, isAdmin, isVolunteer } = useAuth()
  const { t } = useLocale()
  const navigate = useNavigate()
  const [shifts, setShifts] = useState<Shift[]>([])
  const [loading, setLoading] = useState(true)
  const [exportLoading, setExportLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed' | 'cancelled'>('all')
  const [error, setError] = useState('')
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [shiftToDelete, setShiftToDelete] = useState<Shift | null>(null)
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [shiftToDuplicate, setShiftToDuplicate] = useState<Shift | null>(null)
  const [claimingShiftId, setClaimingShiftId] = useState<string | null>(null)
  const [claimedShifts, setClaimedShifts] = useState<Set<string>>(new Set())
  const [isClaimConfirmModalOpen, setIsClaimConfirmModalOpen] = useState(false)
  const [shiftToClaim, setShiftToClaim] = useState<Shift | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [calendarView, setCalendarView] = useState<'monthly' | 'daily'>('monthly')
  /** Whether the opening day has been chosen, so a refetch cannot move the visitor. */
  const pickedOpeningDate = useRef(false)

  // Helper function to check if current user has claimed a shift
  const hasClaimedShift = (shiftId: string): boolean => {
    return claimedShifts.has(shiftId)
  }

  const fetchShifts = useCallback(async () => {
    try {
      setLoading(true)
      
      if (!isVolunteer && !isAdmin) {
        setShifts([])
        setLoading(false)
        return
      }
      
      const statusParam = statusFilter !== 'all' ? statusFilter : undefined
      let data = await api.shifts.list(statusParam) || []

      if (!isAdmin && profile?.id) {
        const myAssignments = await api.shifts.getMyAssignments()
        const assignedShiftIds = new Set((myAssignments || []).map((a: any) => a.shift_id))
        data = data.filter((shift: any) =>
          shift.shift_type === 'public' || assignedShiftIds.has(shift.id)
        )
        const userSkills = profile?.skills || []
        data = data.filter((shift: any) => {
          if (!shift.required_skills || shift.required_skills.length === 0) return true
          if (!userSkills || userSkills.length === 0) return false
          return shift.required_skills.some((s: string) => userSkills.includes(s))
        })
      }
      
      setShifts(data)

      const dates = new Set<string>()
      data.forEach((shift: any) => {
        dates.add(formatDayDateToLocal(shift.start_time, 'yyyy-MM-dd'))
      })
      // Only on the first load: re-deciding on every refetch would throw away the day
      // and month the visitor had navigated to whenever they touched a filter.
      const sortedDates = Array.from(dates).sort()
      if (!pickedOpeningDate.current && sortedDates.length > 0) {
        pickedOpeningDate.current = true
        const today = formatDayDateToLocal(new Date().toISOString(), 'yyyy-MM-dd')
        const opening = sortedDates.includes(today) ? today : sortedDates[0]
        setSelectedDate(opening)
        // An event is over in a month but the record lives on for years, so today's
        // month is usually empty. Open the calendar where the shifts actually are,
        // otherwise the page looks like it has no data at all.
        setCurrentMonth(new Date(`${opening}T00:00:00`))
      }

      if (profile?.id) {
        const myAssignments = await api.shifts.getMyAssignments()
        const claimedSet = new Set((myAssignments || []).filter((a: any) => ['assigned', 'confirmed', 'completed'].includes(a.status)).map((a: any) => a.shift_id))
        setClaimedShifts(claimedSet)
      }
    } catch (err: any) {
      console.error('Error in fetchShifts:', err)
      setError(err.message || 'Failed to fetch shifts')
    } finally {
      setLoading(false)
    }
  }, [profile?.id, profile?.skills, isVolunteer, isAdmin, statusFilter])


  // Declared before the month grid below, which reads it while the component body runs
  // rather than from an event handler.
  const filteredShifts = shifts.filter(shift => {
    const matchesSearch = shift.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         shift.event?.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         shift.location.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || shift.status === statusFilter

    return matchesSearch && matchesStatus
  })

  const getDateDisplayText = (dateKey: string) => {
    const date = new Date(dateKey + 'T00:00:00')
    const today = new Date()
    const tomorrow = addDays(today, 1)
    const yesterday = subDays(today, 1)

    if (isSameDay(date, today)) return 'Today'
    if (isSameDay(date, tomorrow)) return 'Tomorrow'
    if (isSameDay(date, yesterday)) return 'Yesterday'
    
    return formatDayDateToLocal(dateKey + 'T00:00:00')
  }

  const getShiftsForSelectedDate = () => {
    if (!selectedDate) return []
    return filteredShifts.filter(shift => {
      const shiftDate = formatDayDateToLocal(shift.start_time, 'yyyy-MM-dd')
      return shiftDate === selectedDate
    })
  }

  // Calendar helper functions
  const getShiftsForDate = (date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd')
    return filteredShifts.filter(shift => {
      const shiftDate = formatDayDateToLocal(shift.start_time, 'yyyy-MM-dd')
      return shiftDate === dateKey
    })
  }

  const monthStart = startOfMonth(currentMonth)

  // CalendarMonth counts Monday as 1; date-fns counts Sunday as 0.
  const monthFirstWeekday = ((getDay(monthStart) + 6) % 7) + 1

  const monthDays: CalendarDay[] = eachDayOfInterval({
    start: startOfDay(monthStart),
    end: startOfDay(endOfMonth(currentMonth)),
  }).map((day) => {
    const dayShifts = getShiftsForDate(day)
    return {
      date: day.getDate(),
      shifts: dayShifts.length,
      open: dayShifts.filter((s) => s.current_volunteers < s.max_volunteers).length,
      selected: selectedDate === format(day, 'yyyy-MM-dd'),
    }
  })

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentMonth(prev => subDays(startOfMonth(prev), 1))
    } else {
      setCurrentMonth(prev => addDays(endOfMonth(prev), 1))
    }
  }

  const navigateDay = (direction: 'prev' | 'next') => {
    if (!selectedDate) return
    
    const currentDate = new Date(selectedDate + 'T00:00:00')
    const newDate = direction === 'prev' 
      ? subDays(currentDate, 1)
      : addDays(currentDate, 1)
    
    const newDateKey = format(newDate, 'yyyy-MM-dd')
    setSelectedDate(newDateKey)
    
    // Also update the month view to show the correct month
    setCurrentMonth(newDate)
  }

  useEffect(() => {
    if (isVolunteer || isAdmin) {
      fetchShifts()
    } else {
      setError('You need to be a volunteer or admin to view this page')
      setLoading(false)
    }
  }, [isVolunteer, isAdmin, fetchShifts, profile?.id])


  const handleViewDetails = (shift: Shift) => {
    setSelectedShift(shift)
    setIsDetailsModalOpen(true)
  }

  const handleShiftUpdated = () => {
    fetchShifts() // Refresh the shifts list
  }

  const handleDeleteShift = (shift: Shift) => {
    setShiftToDelete(shift)
    setIsDeleteModalOpen(true)
  }

  const handleDuplicateShift = (shift: Shift) => {
    setShiftToDuplicate(shift)
    setIsDuplicateModalOpen(true)
  }

  const confirmDeleteShift = async () => {
    if (!shiftToDelete) return

    try {
      // Send email notifications to assigned volunteers before deleting the shift
      try {
        const assignments = await api.shifts.getAssignments(shiftToDelete.id)
        const assigned = (assignments || []).filter((a: any) => ['assigned', 'confirmed'].includes(a.status))
        for (const assignment of assigned) {
          try {
            await api.notifications.send('shift_deleted', {
              volunteerEmail: assignment.email,
              volunteerFirstName: assignment.first_name,
              volunteerLastName: assignment.last_name,
              shiftTitle: shiftToDelete.title,
              shiftDescription: shiftToDelete.description || '',
              shiftStartTime: shiftToDelete.start_time,
              shiftEndTime: shiftToDelete.end_time,
              shiftLocation: shiftToDelete.location,
              eventTitle: shiftToDelete.event?.title,
              deletedAt: new Date().toISOString(),
              formattedStartTime: new Date(shiftToDelete.start_time).toLocaleString('nl-NL'),
              formattedEndTime: new Date(shiftToDelete.end_time).toLocaleString('nl-NL'),
              formattedDeletedDate: new Date().toLocaleDateString('nl-NL')
            })
          } catch (emailError) {
            console.error('Error sending shift deletion email to volunteer:', assignment.email, emailError)
          }
        }
      } catch (emailError) {
        console.error('Error sending shift deletion emails:', emailError)
      }

      await api.shifts.delete(shiftToDelete.id)

      fetchShifts()
      setShiftToDelete(null)
    } catch (err: any) {
      console.error('Error deleting shift:', err)
      setError(err.message || 'Failed to delete shift')
    }
  }

  const handleClaimShiftClick = (shift: Shift) => {
    setShiftToClaim(shift)
    setIsClaimConfirmModalOpen(true)
  }

  const handleConfirmClaim = async () => {
    if (!shiftToClaim) return

    try {
      setClaimingShiftId(shiftToClaim.id)
      setError('')

      // Check if shift is already full
      if (shiftToClaim.current_volunteers >= shiftToClaim.max_volunteers) {
        setError(t('errors.shiftFull'))
        return
      }

      // Use the upsert function to handle existing assignments
      await api.shifts.upsertAssignment(shiftToClaim.id, profile?.id, 'assigned')

      setError('')
      setClaimedShifts(prev => new Set(Array.from(prev).concat(shiftToClaim.id)))
      fetchShifts()
    } catch (err: any) {
      console.error('Error claiming shift:', err)
      setError(err.message || t('errors.failedToClaim'))
    } finally {
      setClaimingShiftId(null)
      setIsClaimConfirmModalOpen(false)
      setShiftToClaim(null)
    }
  }

  const handleClaimShift = async (shift: Shift) => {
    if (!profile?.id) {
      setError('You must be logged in to claim a shift')
      return
    }

    setClaimingShiftId(shift.id)
    setError('') // Clear any previous errors

    try {
      // Check if shift is already full
      if (shift.current_volunteers >= shift.max_volunteers) {
        setError(t('errors.shiftFull'))
        return
      }

      // Use the upsert function to handle existing assignments
      await api.shifts.upsertAssignment(shift.id, profile.id, 'assigned')

      setError('')
      setClaimedShifts(prev => new Set(Array.from(prev).concat(shift.id)))
      fetchShifts()
    } catch (err: any) {
      console.error('Error claiming shift:', err)
      setError(err.message || t('errors.failedToClaim'))
    } finally {
      setClaimingShiftId(null)
    }
  }

  const exportShiftsToExcel = async () => {
    if (!isAdmin) {
      setError('Only administrators can export shifts')
      return
    }

    setExportLoading(true)
    setError('')

    try {
      const shiftsData = await api.shifts.list() || []
      const shiftsWithVolunteers = await Promise.all(
        shiftsData.map(async (shift: any) => {
          const assignments = await api.shifts.getAssignments(shift.id)
          const nonCancelled = (assignments || []).filter((a: any) => a.status !== 'cancelled')
          return { ...shift, assignments: nonCancelled }
        })
      )

      // Prepare data for Excel export
      const exportData = shiftsWithVolunteers.map(shift => {
        const volunteers = shift.assignments.map((assignment: any) => 
          `${assignment.first_name || ''} ${assignment.last_name || ''} (${assignment.email || ''})`
        ).join('; ')

        return {
          'Shift Title': shift.title,
          'Event': shift.event?.title || 'No Event',
          'Start Date': formatDayDateToLocal(shift.start_time, 'dd/MM/yyyy'),
          'Start Time': formatDayDateToLocal(shift.start_time, 'HH:mm'),
          'End Date': formatDayDateToLocal(shift.end_time, 'dd/MM/yyyy'),
          'End Time': formatDayDateToLocal(shift.end_time, 'HH:mm'),
          'Location': shift.location,
          'Description': shift.description || '',
          'Max Volunteers': shift.max_volunteers,
          'Current Volunteers': shift.current_volunteers,
          'Status': shift.status,
          'Shift Type': shift.shift_type || 'public',
          'Assigned Volunteers': volunteers,
          'Created At': formatDayDateToLocal(shift.created_at, 'dd/MM/yyyy HH:mm'),
          'Updated At': formatDayDateToLocal(shift.updated_at, 'dd/MM/yyyy HH:mm')
        }
      }) || []

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(exportData)

      // Auto-size columns
      const colWidths = [
        { wch: 20 }, // Shift Title
        { wch: 15 }, // Event
        { wch: 12 }, // Start Date
        { wch: 10 }, // Start Time
        { wch: 12 }, // End Date
        { wch: 10 }, // End Time
        { wch: 20 }, // Location
        { wch: 30 }, // Description
        { wch: 12 }, // Max Volunteers
        { wch: 15 }, // Current Volunteers
        { wch: 10 }, // Status
        { wch: 10 }, // Shift Type
        { wch: 50 }, // Assigned Volunteers
        { wch: 18 }, // Created At
        { wch: 18 }  // Updated At
      ]
      ws['!cols'] = colWidths

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Shifts')

      // Generate filename with current date
      const now = new Date()
      const dateStr = format(now, 'yyyy-MM-dd_HH-mm')
      const filename = `shifts_export_${dateStr}.xlsx`

      // Save file
      XLSX.writeFile(wb, filename)

    } catch (err: any) {
      console.error('Error exporting shifts:', err)
      setError(err.message || 'Failed to export shifts')
    } finally {
      setExportLoading(false)
    }
  }

  const getStaffingIndicatorColor = (shift: Shift) => {
    return shift.current_volunteers < shift.max_volunteers
      ? 'bg-capacity-open'
      : 'bg-capacity-full'
  }

  // Calculate dynamic time range based on shifts
  const getTimeRange = (shifts: Shift[]): { startHour: number; endHour: number; totalMinutes: number } => {
    if (shifts.length === 0) {
      return { startHour: 0, endHour: 24, totalMinutes: 1440 }
    }

    let earliestStart = 24 * 60 // Start with end of day
    let latestEnd = 0 // Start with beginning of day

    shifts.forEach(shift => {
      const start = new Date(shift.start_time)
      const end = new Date(shift.end_time)
      const startMinutes = start.getHours() * 60 + start.getMinutes()
      const endMinutes = end.getHours() * 60 + end.getMinutes()

      earliestStart = Math.min(earliestStart, startMinutes)
      latestEnd = Math.max(latestEnd, endMinutes)
    })

    // Add some padding (30 minutes before and after)
    const padding = 30
    const startHour = Math.max(0, Math.floor((earliestStart - padding) / 60))
    const endHour = Math.min(24, Math.ceil((latestEnd + padding) / 60))
    const totalMinutes = (endHour - startHour) * 60

    return { startHour, endHour, totalMinutes }
  }

  const getShiftsForSelectedDateSorted = (): Shift[] => {
    if (!selectedDate) return []
    return filteredShifts
      .filter(shift => {
        const shiftDate = formatDayDateToLocal(shift.start_time, 'yyyy-MM-dd')
        return shiftDate === selectedDate
      })
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
  }

  // Calculate column positions for overlapping shifts
  const calculateShiftColumns = (shifts: Shift[]): Array<{ shift: Shift; column: number; totalColumns: number }> => {
    if (shifts.length === 0) return []
    
    const positionedShifts: Array<{ shift: Shift; column: number; endTime: number }> = []
    const columns: number[] = [] // Track when each column becomes free
    
    shifts.forEach(shift => {
      const startTime = new Date(shift.start_time).getTime()
      const endTime = new Date(shift.end_time).getTime()
      
      // Find the first available column
      let column = 0
      while (column < columns.length && columns[column] > startTime) {
        column++
      }
      
      // If no column is available, create a new one
      if (column >= columns.length) {
        columns.push(endTime)
      } else {
        columns[column] = endTime
      }
      
      positionedShifts.push({ shift, column, endTime })
    })
    
    return positionedShifts.map(({ shift, column }) => ({
      shift,
      column,
      totalColumns: columns.length
    }))
  }

  const dayViewRange = getTimeRange(getShiftsForSelectedDateSorted())

  /** The selected day's shifts as DayView blocks, with overlaps already laned. */
  const dayViewShifts: DayViewShift[] = calculateShiftColumns(
    getShiftsForSelectedDateSorted(),
  ).map(({ shift, column, totalColumns }) => {
    const start = new Date(shift.start_time)
    const end = new Date(shift.end_time)
    return {
      id: shift.id,
      title: hasClaimedShift(shift.id) ? `\u2713 ${shift.title}` : shift.title,
      start: start.getHours() + start.getMinutes() / 60,
      end: end.getHours() + end.getMinutes() / 60,
      status: shift.status as DayViewShift['status'],
      filled: shift.current_volunteers,
      capacity: shift.max_volunteers,
      lane: column,
      lanes: totalColumns,
    }
  })


  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-tenant-brand border-r-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="font-display text-display-lg text-text-primary">{t('shifts.title')}</h1>
          <p className="mt-2 text-md text-text-secondary">{t('shifts.subtitle')}</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          {isAdmin && (
            <div className="flex gap-2">
              <Button onClick={() => navigate('/shifts/new')} icon={<Plus className="h-4 w-4" />}>
                {t('shifts.addShift')}
              </Button>
              <Button
                variant="secondary"
                onClick={exportShiftsToExcel}
                loading={exportLoading}
                icon={<Download className="h-4 w-4" />}
              >
                {exportLoading ? t('shifts.exporting') : t('shifts.export')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-5 w-5 text-text-muted" />
          </div>
          <Input
            className="pl-10"
            placeholder={t('shifts.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            options={[
              { value: 'all', label: t('shifts.filterAll') },
              { value: 'active', label: t('shifts.filterActive') },
              { value: 'completed', label: t('shifts.filterCompleted') },
              { value: 'cancelled', label: t('shifts.filterCancelled') },
            ]}
          />
          <Filter className="pointer-events-none absolute right-2 top-3 h-4 w-4 text-text-muted" />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-surface-card p-4 shadow-card">
        <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center justify-between sm:justify-start sm:space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => calendarView === 'monthly' ? navigateMonth('prev') : navigateDay('prev')}
                className="rounded-md border border-border bg-surface-card p-2 text-text-secondary hover:bg-surface-sunken"
                title={calendarView === 'monthly' ? t('shifts.previousMonth') : t('shifts.previousDay')}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              <button
                onClick={() => calendarView === 'monthly' ? navigateMonth('next') : navigateDay('next')}
                className="rounded-md border border-border bg-surface-card p-2 text-text-secondary hover:bg-surface-sunken"
                title={calendarView === 'monthly' ? t('shifts.nextMonth') : t('shifts.nextDay')}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            
            <h2 className="flex-1 text-center font-display text-display-sm text-text-primary sm:flex-none sm:text-left">
              {calendarView === 'monthly'
                ? format(currentMonth, 'MMMM yyyy')
                : selectedDate
                  ? getDateDisplayText(selectedDate)
                  : t('shifts.selectDateToView')
              }
            </h2>
          </div>

          <div className="flex items-center justify-center gap-2 sm:justify-end">
            <Tabs
              items={[
                { value: 'monthly', label: t('shifts.monthly') },
                { value: 'daily', label: t('shifts.daily') },
              ]}
              active={calendarView}
              onChange={(value) => setCalendarView(value as 'monthly' | 'daily')}
            />
            <Button
              variant="ghost"
              onClick={() => {
                const today = format(new Date(), 'yyyy-MM-dd')
                setSelectedDate(today)
                setCurrentMonth(new Date())
              }}
            >
              {t('shifts.today')}
            </Button>
          </div>
        </div>

        {/* Calendar Content */}
        {calendarView === 'monthly' ? (
          <CalendarMonth
            monthLabel={format(currentMonth, 'MMMM yyyy')}
            firstWeekday={monthFirstWeekday}
            days={monthDays}
            onSelect={(day) =>
              setSelectedDate(format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day.date), 'yyyy-MM-dd'))
            }
            onPrev={() => navigateMonth('prev')}
            onNext={() => navigateMonth('next')}
            prevLabel={t('shifts.previousMonth')}
            nextLabel={t('shifts.nextMonth')}
          />
        ) : (
          <div className="flex flex-col">
            <div className="flex-1 relative">
              <div className="flex h-8 items-center justify-center border-b border-border text-sm font-semibold text-text-primary">
                {selectedDate ? getDateDisplayText(selectedDate) : t('shifts.selectDateToView')}
              </div>

              {/* Desktop: hour grid. Mobile keeps the stacked list below. */}
              <div className="hidden pt-4 lg:block">
                <DayView
                  startHour={dayViewRange.startHour}
                  endHour={dayViewRange.endHour}
                  shifts={dayViewShifts}
                  emptyLabel={t('shifts.selectDateToView')}
                  onShiftClick={(block) => {
                    const shift = filteredShifts.find((s) => s.id === block.id)
                    if (!shift) return
                    if (!hasClaimedShift(shift.id) && shift.current_volunteers < shift.max_volunteers) {
                      handleClaimShiftClick(shift)
                    } else {
                      handleViewDetails(shift)
                    }
                  }}
                />
              </div>

              {/* Mobile Calendar View - Stacked Layout */}
              <div className="lg:hidden">
                {selectedDate ? (
                  <div className="space-y-3 p-4">
                    {getShiftsForSelectedDateSorted().map((shift) => {
                      const claimed = hasClaimedShift(shift.id)
                      const full = shift.current_volunteers >= shift.max_volunteers
                      return (
                        <div
                          key={shift.id}
                          className="rounded-lg border border-border bg-surface-card p-3 shadow-card"
                        >
                          <button
                            type="button"
                            onClick={() => handleViewDetails(shift)}
                            className="w-full text-left"
                          >
                            <div className="mb-2 flex items-start justify-between gap-2">
                              <h3 className="text-md font-semibold text-text-primary">
                                {shift.title}
                              </h3>
                              <StatusBadge
                                dimension="shift"
                                status={shift.status}
                                size="sm"
                                label={statusLabel('shift', shift.status)}
                              />
                            </div>
                            <div className="mb-1 text-sm text-text-secondary">
                              {formatTimeRangeToLocal(shift.start_time, shift.end_time)}
                            </div>
                            <div className="mb-1 flex items-center gap-1 text-sm text-text-muted">
                              <MapPin className="h-3.5 w-3.5" />
                              {shift.location}
                            </div>
                          </button>

                          <div className="mb-3 mt-2">
                            <Capacity
                              value={shift.current_volunteers}
                              max={shift.max_volunteers}
                              stateLabels={CAPACITY_LABELS}
                            />
                          </div>

                          {!claimed && !full ? (
                            <Button
                              variant="tenant"
                              size="lg"
                              fullWidth
                              loading={claimingShiftId === shift.id}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleClaimShiftClick(shift)
                              }}
                            >
                              {claimingShiftId === shift.id ? t('shifts.claiming') : t('shifts.claimShift')}
                            </Button>
                          ) : (
                            <div className="flex min-h-touch-comfort w-full items-center justify-center rounded-md border border-border bg-surface-sunken text-md font-semibold text-text-muted">
                              {claimed ? t('shifts.claimed') : t('errors.shiftFull')}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center text-text-muted">
                    {t('shifts.selectDateToViewMessage')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div>
        {filteredShifts.length === 0 ? (
          <div className="py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-text-muted" />
            <h3 className="mt-2 text-md font-semibold text-text-primary">{t('shifts.noShifts')}</h3>
            <p className="mt-1 text-sm text-text-secondary">
              {searchTerm || statusFilter !== 'all'
                ? t('shifts.tryAdjustingSearch')
                : t('shifts.noShiftsCreatedYet')}
            </p>
          </div>
        ) : selectedDate ? (
          <div className="rounded-lg border border-border bg-surface-card shadow-card">
            <div className="rounded-t-lg border-b border-border bg-surface-sunken px-4 py-3">
              <h3 className="text-sm font-semibold text-text-primary">
                {getDateDisplayText(selectedDate)}
              </h3>
            </div>

            {getShiftsForSelectedDate().length === 0 ? (
              <div className="py-8 text-center">
                <Calendar className="mx-auto h-8 w-8 text-text-muted" />
                <h3 className="mt-2 text-md font-semibold text-text-primary">{t('shifts.noShiftsOnDate')}</h3>
                <p className="mt-1 text-sm text-text-secondary">
                  {t('shifts.noShiftsOnDateMessage')}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {getShiftsForSelectedDate().map((shift) => (
                  <li key={shift.id} className="relative">
                    {/* Staffing indicator */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStaffingIndicatorColor(shift)}`}></div>
                    
                    <div className="px-4 py-4 pl-5">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            {/* Two lines before it gives up: the title is what the row is
                                for, and a name like "01 Schoolschaatsen Begeleiding" must
                                not lose its second half to make room for buttons. */}
                            <h3 className="line-clamp-2 text-lg font-semibold text-text-primary">
                              {shift.title}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2">
                              <StatusBadge
                                dimension="shift"
                                status={shift.status}
                                label={statusLabel('shift', shift.status)}
                              />
                              {shift.shift_type === 'private' && (
                                <StatusBadge
                                  dimension="visibility"
                                  status="private"
                                  label={t('shifts.private')}
                                />
                              )}
                              {isUpcomingInLocal(shift.start_time) && isTodayInLocal(shift.start_time) && (
                                <span className="rounded-pill bg-status-warning-bg px-3 py-1 text-[13px] font-semibold text-status-warning-text">
                                  {t('shifts.today')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 flex flex-col space-y-1 text-sm text-text-secondary sm:flex-row sm:items-center sm:space-x-6 sm:space-y-0">
                            <div className="flex items-center">
                              <Clock className="mr-1 h-4 w-4 flex-shrink-0" />
                              {formatTimeRangeToLocal(shift.start_time, shift.end_time)}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="mr-1 h-4 w-4 flex-shrink-0" />
                              <span className="truncate">{shift.location}</span>
                            </div>
                            <div className="flex items-center">
                              <Users className="mr-1 h-4 w-4 flex-shrink-0" />
                              {shift.current_volunteers}/{shift.max_volunteers} {t('shifts.volunteers')}
                            </div>
                          </div>

                          <div className="mt-2 max-w-xs">
                            <Capacity
                              value={shift.current_volunteers}
                              max={shift.max_volunteers}
                              stateLabels={CAPACITY_LABELS}
                            />
                          </div>

                          {shift.required_skills && shift.required_skills.length > 0 && (
                            <div className="mt-2">
                              <div className="mb-1 flex items-center">
                                <Award className="mr-1 h-3 w-3 text-text-muted" />
                                <span className="text-xs font-semibold text-text-muted">{t('shifts.requiredSkills')}</span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {shift.required_skills.map((skill, index) => (
                                  <Tag key={index}>{skill}</Tag>
                                ))}
                              </div>
                            </div>
                          )}
                          {shift.event && (
                            <div className="mt-1 text-sm text-text-secondary">
                              {t('shifts.event')}: {shift.event.title}
                            </div>
                          )}
                          {shift.description && (
                            <div className="mt-2 text-sm text-text-secondary">
                              {shift.description}
                            </div>
                          )}
                        </div>
                        {/* Six labelled buttons left the title about a third of the row and
                            truncated most of them, so only the two actions a visitor
                            actually chooses between keep their words. */}
                        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                          <Button variant="secondary" onClick={() => handleViewDetails(shift)}>
                            {t('shifts.viewDetails')}
                          </Button>

                          {isAdmin && (
                            <>
                              <Button
                                variant="secondary"
                                iconOnly
                                aria-label={t('shifts.assign')}
                                title={t('shifts.assign')}
                                onClick={() => navigate(`/shifts/${shift.id}/matching`)}
                                icon={<Sparkles className="h-4 w-4" />}
                              />
                              <Button
                                variant="secondary"
                                iconOnly
                                aria-label={t('shifts.edit')}
                                title={t('shifts.edit')}
                                onClick={() => navigate(`/shifts/${shift.id}/edit`)}
                                icon={<Edit className="h-4 w-4" />}
                              />
                              <Button
                                variant="secondary"
                                iconOnly
                                aria-label={t('shifts.duplicate')}
                                title={t('shifts.duplicate')}
                                onClick={() => handleDuplicateShift(shift)}
                                icon={<Copy className="h-4 w-4" />}
                              />
                              <Button
                                variant="danger"
                                iconOnly
                                aria-label={t('shifts.delete')}
                                title={t('shifts.delete')}
                                onClick={() => handleDeleteShift(shift)}
                                icon={<Trash2 className="h-4 w-4" />}
                              />
                            </>
                          )}

                          {isVolunteer && !hasClaimedShift(shift.id) && (
                            <Button
                              variant="tenant"
                              onClick={() => handleClaimShift(shift)}
                              loading={claimingShiftId === shift.id}
                              disabled={shift.current_volunteers >= shift.max_volunteers}
                            >
                              {claimingShiftId === shift.id ? t('shifts.claiming') : t('shifts.claimShift')}
                            </Button>
                          )}

                          {isVolunteer && hasClaimedShift(shift.id) && (
                            <StatusBadge
                              dimension="assignment"
                              status="assigned"
                              label={t('shifts.claimed')}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="py-12 text-center">
            <Calendar className="mx-auto h-12 w-12 text-text-muted" />
            <h3 className="mt-2 text-md font-semibold text-text-primary">{t('shifts.selectDate')}</h3>
            <p className="mt-1 text-sm text-text-secondary">
              {t('shifts.selectDateMessage')}
            </p>
          </div>
        )}
      </div>

      {/* Modals */}
      <ShiftDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false)
          setSelectedShift(null)
        }}
        shift={selectedShift}
        onShiftUpdated={handleShiftUpdated}
        userRole={profile?.role}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false)
          setShiftToDelete(null)
        }}
        onConfirm={confirmDeleteShift}
        title={t('deleteConfirm.title')}
        message={t('deleteConfirm.message')}
        itemName={shiftToDelete ? `${shiftToDelete.title} (${formatDayDateToLocal(shiftToDelete.start_time, 'd MMM yyyy')})` : ''}
      />

      {/* Duplicate Shift Modal */}
      <DuplicateShiftModal
        isOpen={isDuplicateModalOpen}
        onClose={() => {
          setIsDuplicateModalOpen(false)
          setShiftToDuplicate(null)
        }}
        shift={shiftToDuplicate}
        onShiftDuplicated={handleShiftUpdated}
      />

      {/* Claim Confirmation Modal */}
      <ClaimConfirmModal
        isOpen={isClaimConfirmModalOpen}
        onClose={() => {
          setIsClaimConfirmModalOpen(false)
          setShiftToClaim(null)
        }}
        onConfirm={handleConfirmClaim}
        shift={shiftToClaim}
        isLoading={claimingShiftId === shiftToClaim?.id}
      />
    </div>
  )
}

export default ShiftsPage
